import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { UltraluxUSPDefinitions } from 'constants/ultraLux'
import GeoContext from 'contexts/geoContext'
import { rem } from 'polished'
import React, { useContext } from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import eases from 'styles/tools/eases'
import ContactContext from 'contexts/contactContext'

const ItemTransition = keyframes`
  0% {
    transform: translateX(0);
  }

  45% {
    transform: translateX(0);
  }

  /* Transition to slide 2 */

  50% {
    transform: translateX(calc(-100% - ${rem(16)}));
  }

  95% {
    transform: translateX(calc(-100% - ${rem(16)}));
  }

  /* Transition to slide 1 */

  100% {
    transform: translateX(0);
  }
`

const UltraLuxUSPs = styled.div`
  display: grid;
  gap: ${rem(20)};
  padding-top: ${rem(36)};
  padding-bottom: ${rem(36)};
  overflow: visible;

  ${mediaQueryUp.tablet} {
    animation: none;
    grid-template-columns: unset;
    grid-auto-columns: 1fr;
    grid-auto-flow: column;
    padding-top: ${rem(56)};
  }

  &.carousel {
    animation: ${ItemTransition} 8s infinite ${eases.power2.inOut};
    grid-template-columns: repeat(2, 100%);

    ${mediaQueryUp.tablet} {
      animation: none;
      grid-template-columns: unset;
    }
  }
`

const UltraLuxUSP = styled.div`
  display: grid;
  gap: ${rem(32)};
  grid-template-columns: ${rem(120)} 1fr;
  align-items: center;
  height: ${rem(120)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(20)};
  }
`

const AnimationContainer = styled(VerticalSpacer)`
  overflow: hidden;
  margin-left: -${rem(16)};
  margin-right: -${rem(16)};
  padding-left: ${rem(16)};
  padding-right: ${rem(16)};

  > ${mediaQueryUp.tablet} {
    margin-left: 0;
    margin-right: 0;
    padding-left: 0;
    padding-right: 0;
  }
`

interface Props {
  verticalType: 'hotels' | 'tours' | 'cruises'
}

function UltraLuxVerticalPageUSPs(props: Props) {
  const { verticalType } = props
  const intl = useIntl()
  const currentContact = useContext(ContactContext)
  const contact = currentContact.defaultContact

  const { currentRegionCode } = useContext(GeoContext)

  const premiumUsp = UltraluxUSPDefinitions[verticalType].premium(currentRegionCode)
  const serviceUsp = UltraluxUSPDefinitions[verticalType].service(currentRegionCode)

  return (
    <AnimationContainer gap={20}>
      <UltraLuxUSPs className="carousel">
        <UltraLuxUSP>
          <Image fit="center" id={premiumUsp.imageId} height={120} dpr={2} />
          <VerticalSpacer gap={4}>
            <BodyText variant="medium" weight="bold" colour="neutral-two">
              {premiumUsp.title}
            </BodyText>
            <BodyText variant="medium" colour="neutral-three">
              {premiumUsp.subtitle}
            </BodyText>
          </VerticalSpacer>
        </UltraLuxUSP>
        <UltraLuxUSP>
          <Image fit="center" id={serviceUsp.imageId} height={120} dpr={2} />
          <VerticalSpacer gap={12}>
            <VerticalSpacer gap={4}>
              <BodyText variant="medium" weight="bold" colour="neutral-two">
                {serviceUsp.title}
              </BodyText>
              <BodyText variant="medium" colour="neutral-three">
                {serviceUsp.subtitle}
              </BodyText>
            </VerticalSpacer>
            <TextLink size="medium" href={`tel:${contact.number}`}>
              {intl.formatMessage(
                {
                  id: 'LWu8WK63Kc',
                  defaultMessage: 'Call {phoneNumber}',
                  description: 'Link text to call Ultra Lux concierge support number',
                },
                { phoneNumber: contact.humanReadable },
              )}
            </TextLink>
          </VerticalSpacer>
        </UltraLuxUSP>
      </UltraLuxUSPs>
    </AnimationContainer>
  )
}

export default UltraLuxVerticalPageUSPs
